"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { findImprovementsQueryKey, qualitySuggestionsQueryKey } from "@/hooks/queries/conversation-quality"
import { agentSelfLearningQueryKey } from "@/hooks/queries/self-learning"
import { withQueryHandling } from "@/lib/with-query-handling"
import {
  setSelfLearningArticlesAction,
  setSelfLearningModeAction,
  type SelfLearningView,
  type SetSelfLearningModeInput,
} from "@/server-actions/self-learning"

type SetErrors = (errors: Record<string, unknown>) => void

/** Off closes the agent's waiting drafts and stops its presses, so both lists are read again. */
export function useSetSelfLearningModeMutation(agentId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      input,
      setErrors,
    }: {
      input: Omit<SetSelfLearningModeInput, "agentId">;
      setErrors?: SetErrors;
    }) => {
      const result = await withQueryHandling(setSelfLearningModeAction({ agentId, ...input }), {
        loading: "Saving Self-learning...",
        success: "Self-learning saved",
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (state) => {
      queryClient.setQueryData<SelfLearningView>(
        agentSelfLearningQueryKey(agentId),
        (view) => view && { ...view, ...state }
      )
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: agentSelfLearningQueryKey(agentId) }),
        queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey }),
        queryClient.invalidateQueries({ queryKey: findImprovementsQueryKey }),
      ]),
  })
}

export function useSetSelfLearningArticlesMutation(agentId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (addsArticles: boolean) => {
      const result = await withQueryHandling(setSelfLearningArticlesAction({ agentId, addsArticles }), {
        loading: "Saving Self-learning...",
        success: "Self-learning saved",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (state) => {
      queryClient.setQueryData<SelfLearningView>(
        agentSelfLearningQueryKey(agentId),
        (view) => view && { ...view, ...state }
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: agentSelfLearningQueryKey(agentId) }),
  })
}
